"use client";

import React, { createContext, useContext, useReducer, ReactNode, useEffect, useRef } from "react";
import { Dashboard, DateRange } from "../types";
import { Dealer, getDealerContractedProducts, GetDealerContractedProductsResponse } from "@/modules/dealer";
import { useAuth, STORAGE_KEY_PREFIX } from "@/modules/auth";

interface DashboardState {
  // Dashboard level
  currentDashboard: Dashboard | null;
  dashboards: Dashboard[];

  // Selection state
  selectedDealer: Dealer | null;
  selectedDateRange: DateRange | null;
  selectedCompareEnabled: boolean;
  selectedCompareDateRange: DateRange | null;

  dealerContractedProducts: GetDealerContractedProductsResponse | null;

  // UI state
  loading: boolean;
  error: string | null;
  isEditMode: boolean;
  hydrated: boolean;
}

// Action types
type DashboardAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_CURRENT_DASHBOARD"; payload: Dashboard | null }
  | { type: "ADD_DASHBOARD"; payload: Dashboard }
  | { type: "UPDATE_DASHBOARD"; payload: Dashboard }
  | { type: "SET_SELECTED_DEALER"; payload: Dealer | null }
  | { type: "SET_SELECTED_DATE_RANGE"; payload: DateRange | null }
  | { type: "SET_SELECTED_COMPARE_ENABLED"; payload: boolean }
  | { type: "SET_SELECTED_COMPARE_DATE_RANGE"; payload: DateRange | null }
  | { type: "SET_DEALER_CONTRACTED_PRODUCTS"; payload: GetDealerContractedProductsResponse | null }
  | { type: "SET_DEALER_CONTRACTED_PRODUCTS_LOADING"; payload: boolean }
  | { type: "SET_DEALER_CONTRACTED_PRODUCTS_ERROR"; payload: string | null }
  | { type: "SET_HYDRATED"; payload: boolean }

// Initial state
const initialState: DashboardState = {
  currentDashboard: null,
  dashboards: [],
  selectedDealer: null,
  selectedDateRange: null,
  selectedCompareEnabled: false,
  selectedCompareDateRange: null,

  dealerContractedProducts: null,

  loading: false,
  error: null,
  isEditMode: false,
  hydrated: false,
};

// Reducer
const dashboardReducer = (state: DashboardState, action: DashboardAction): DashboardState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "SET_CURRENT_DASHBOARD":
      return { ...state, currentDashboard: action.payload };
    case "SET_SELECTED_DEALER":
      return { ...state, selectedDealer: action.payload };
    case "SET_SELECTED_DATE_RANGE":
      return { ...state, selectedDateRange: action.payload };
    case "SET_SELECTED_COMPARE_ENABLED":
      return { ...state, selectedCompareEnabled: action.payload };
    case "SET_SELECTED_COMPARE_DATE_RANGE":
      return { ...state, selectedCompareDateRange: action.payload };
    case "SET_DEALER_CONTRACTED_PRODUCTS":
      return { ...state, dealerContractedProducts: action.payload };
    case "SET_HYDRATED":
      return { ...state, hydrated: action.payload };
    default:
      return state;
  }
};

// Context
const DashboardContext = createContext<{
  state: DashboardState;
  dispatch: React.Dispatch<DashboardAction>;
} | null>(null);
DashboardContext.displayName = "DashboardContext";

// Provider component
interface DashboardProviderProps {
  children: ReactNode;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const { hydrated: authHydrated, user } = useAuth();

  // Date range/compare: sessionStorage first, localStorage as a cross-session fallback (no
  // per-dealer access risk). Dealer is handled separately below via a per-user fallback.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const readKey = (key: string): string | null =>
      sessionStorage.getItem(key) ?? localStorage.getItem(key);

    // Load dealer (tab-local only; see the per-user "last dealer" effect below)
    const storedDealer = sessionStorage.getItem("selectedDealer");
    if (storedDealer) {
      try {
        const dealer = JSON.parse(storedDealer);
        dispatch({ type: "SET_SELECTED_DEALER", payload: dealer });
      } catch {
        sessionStorage.removeItem("selectedDealer");
      }
    }

    const storedCompareEnabled = readKey("selectedCompareEnabled");
    if (storedCompareEnabled) {
      dispatch({ type: "SET_SELECTED_COMPARE_ENABLED", payload: storedCompareEnabled === "true" });
      sessionStorage.setItem("selectedCompareEnabled", storedCompareEnabled);
    }

    const storedCompareDateRange = readKey("selectedCompareDateRange");
    if (storedCompareDateRange) {
      try {
        const dateRange = JSON.parse(storedCompareDateRange);
        const parsedRange = {
          from: new Date(dateRange.from),
          to: new Date(dateRange.to)
        };
        dispatch({ type: "SET_SELECTED_COMPARE_DATE_RANGE", payload: parsedRange });
        sessionStorage.setItem("selectedCompareDateRange", storedCompareDateRange);
      } catch {
        localStorage.removeItem("selectedCompareDateRange");
        sessionStorage.removeItem("selectedCompareDateRange");
      }
    }

    // Load date range
    const storedDateRange = readKey("selectedDateRange");
    if (storedDateRange) {
      try {
        const dateRange = JSON.parse(storedDateRange);
        // Convert string dates back to Date objects
        const parsedRange = {
          from: new Date(dateRange.from),
          to: new Date(dateRange.to)
        };
        dispatch({ type: "SET_SELECTED_DATE_RANGE", payload: parsedRange });
        sessionStorage.setItem("selectedDateRange", storedDateRange);
      } catch {
        localStorage.removeItem("selectedDateRange");
        sessionStorage.removeItem("selectedDateRange");
      }
    }

    dispatch({ type: "SET_HYDRATED", payload: true });
  }, []);

  // Persist dealer to sessionStorage (tab-local) when it changes
  useEffect(() => {
    if (!state.hydrated) return;

    if (state.selectedDealer) {
      sessionStorage.setItem("selectedDealer", JSON.stringify(state.selectedDealer));
    } else {
      sessionStorage.removeItem("selectedDealer");
    }
  }, [state.selectedDealer, state.hydrated]);

  // Seed dealer from this user's durable last-dealer once auth resolves, unless this tab already
  // restored one from sessionStorage. Ref guard: only seeds once per login, never overrides later picks.
  const lastDealerSeedAttempted = useRef(false);
  useEffect(() => {
    if (!state.hydrated || !authHydrated) return;
    if (!user?.userId) return;
    if (lastDealerSeedAttempted.current) return;
    lastDealerSeedAttempted.current = true;

    if (state.selectedDealer) return;

    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}.lastDealer.${user.userId}`);
    if (!stored) return;
    try {
      dispatch({ type: "SET_SELECTED_DEALER", payload: JSON.parse(stored) });
    } catch {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}.lastDealer.${user.userId}`);
    }
  }, [state.hydrated, authHydrated, user?.userId, state.selectedDealer]);

  // Persist as this user's durable last-dealer, namespaced per userId. Not cleared on sign-out
  // (see UserContext.clearSession) — meant to survive logout for this same user.
  useEffect(() => {
    if (!state.hydrated || !authHydrated || !user?.userId || !state.selectedDealer) return;

    localStorage.setItem(`${STORAGE_KEY_PREFIX}.lastDealer.${user.userId}`, JSON.stringify(state.selectedDealer));
  }, [state.selectedDealer, state.hydrated, authHydrated, user?.userId]);

  useEffect(() => {
    if (!state.hydrated) return;

    let canceled = false;

    const dealerRefId = state.selectedDealer?.dealerRefId;
    if (!dealerRefId) {
      dispatch({ type: "SET_DEALER_CONTRACTED_PRODUCTS", payload: null });
      return;
    }

    const dateFrom = state.selectedDateRange?.from ? state.selectedDateRange.from.toISOString().split("T")[0] : undefined;
    const dateTo = state.selectedDateRange?.to ? state.selectedDateRange.to.toISOString().split("T")[0] : undefined;

    getDealerContractedProducts(dealerRefId, dateFrom, dateTo)
      .then((data) => {
        if (canceled) return;
        dispatch({ type: "SET_DEALER_CONTRACTED_PRODUCTS", payload: data ?? null });
      }).catch((error) => {
        console.error("Error fetching dealer contracted products:", error);
        dispatch({ type: "SET_DEALER_CONTRACTED_PRODUCTS", payload: null });
      });

    return () => {
      canceled = true;
    };
  }, [state.selectedDealer, state.selectedDateRange, state.hydrated]);

  // Persist date range to sessionStorage (tab-local) and localStorage (cross-session seed) when it changes
  useEffect(() => {
    if (!state.hydrated) return;

    if (state.selectedDateRange) {
      const serialized = JSON.stringify(state.selectedDateRange);
      sessionStorage.setItem("selectedDateRange", serialized);
      localStorage.setItem("selectedDateRange", serialized);
    } else {
      sessionStorage.removeItem("selectedDateRange");
      localStorage.removeItem("selectedDateRange");
    }
  }, [state.selectedDateRange, state.hydrated]);

  useEffect(() => {
    if (!state.hydrated) return;

    sessionStorage.setItem("selectedCompareEnabled", String(state.selectedCompareEnabled));
    localStorage.setItem("selectedCompareEnabled", String(state.selectedCompareEnabled));
  }, [state.selectedCompareEnabled, state.hydrated]);

  useEffect(() => {
    if (!state.hydrated) return;

    if (state.selectedCompareDateRange) {
      const serialized = JSON.stringify(state.selectedCompareDateRange);
      sessionStorage.setItem("selectedCompareDateRange", serialized);
      localStorage.setItem("selectedCompareDateRange", serialized);
    } else {
      sessionStorage.removeItem("selectedCompareDateRange");
      localStorage.removeItem("selectedCompareDateRange");
    }
  }, [state.selectedCompareDateRange, state.hydrated]);

  return (
    <DashboardContext.Provider value={{ state, dispatch }}>
      {children}
    </DashboardContext.Provider>
  );
};

// Custom hook
export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
};
